import {
  ArrowRight,
  Clock,
  Shield,
  BarChart3,
  Lightbulb,
  Target,
  TrendingUp,
  Eye,
  ChevronDown,
  Layers,
  Compass,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import type { Route } from "./+types/survey._index";

export function meta(): Route.MetaDescriptors {
  return [
    { title: "Disrupting the Future — How Future-Ready Is Your Organisation?" },
    {
      name: "description",
      content:
        "Take the Disrupting the Future survey and benchmark your organisation's future-readiness. Free instant score and archetype. ~25 minutes, anonymous.",
    },
  ];
}

// ─── FAQ data ───

const FAQ_ITEMS = [
  {
    question: "Is it really anonymous?",
    answer:
      "Yes. No login is required. We do not store your IP address — only your country is inferred for aggregate benchmarking. Your individual responses are never shared.",
  },
  {
    question: "How long does it take?",
    answer:
      "Approximately 25 minutes. You can save your progress at any point and return later using a unique resume link.",
  },
  {
    question: "What happens to my data?",
    answer:
      "Your responses are stored securely in EU-hosted infrastructure, fully compliant with UK GDPR. Individual data is never published — only aggregated benchmarks with a minimum cell size of 30 respondents.",
  },
  {
    question: "Who is this for?",
    answer:
      "Senior leaders, board members, strategy directors, and transformation teams in any sector. The instrument is designed for people who shape their organisation's strategic direction.",
  },
  {
    question: "What do I get for free vs. paid?",
    answer:
      "Free: your overall Future-Readiness score, archetype classification, and one tailored observation. Subscribers unlock full sector benchmarks, item-level heatmaps, tailored observations, a downloadable PDF report, and year-on-year tracking.",
  },
];

// ─── Value cards ───

const VALUE_CARDS = [
  {
    icon: Target,
    title: "Instant score & archetype",
    description:
      "Your Future-Readiness Index score (0-100) and archetype classification — immediately on completion.",
  },
  {
    icon: BarChart3,
    title: "Sector benchmarking",
    description:
      "See how your organisation compares to peers in your industry and size band.",
  },
  {
    icon: Lightbulb,
    title: "Tailored observations",
    description:
      "Personalised insights drawn from your response pattern — not generic advice.",
  },
];

// ─── Steps ───

const STEPS = [
  {
    number: "1",
    title: "Answer the questions",
    description: "Nine short sections covering strategic foresight, adaptability, and future forces.",
  },
  {
    number: "2",
    title: "Get your score instantly",
    description: "Your Future-Readiness Index and archetype — Vigilant, Vulnerable, Neurotic, or In Danger.",
  },
  {
    number: "3",
    title: "Unlock the full report",
    description:
      "Subscribe to see sector benchmarks, item-level analysis, and year-on-year tracking.",
  },
];

// ─── Constructs overview ───

const CONSTRUCTS = [
  { icon: Compass, label: "Strategic Foresight" },
  { icon: Eye, label: "Sensing" },
  { icon: Target, label: "Seizing" },
  { icon: TrendingUp, label: "Transforming" },
  { icon: Layers, label: "Ambidexterity" },
  { icon: Sparkles, label: "Peripheral Vision" },
];

// ─── FAQ accordion item ───

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="text-base font-semibold text-white">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-white/60 shrink-0 ml-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <p className="text-white/70 text-sm leading-relaxed pb-5 pr-10">{answer}</p>}
    </div>
  );
}

// ─── Main component ───

export default function SurveyLanding() {
  return (
    <div className="min-h-screen bg-white">
      {/* ─── Nav ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white tracking-tight">
                Disrupting the Future
              </span>
              <span className="hidden sm:inline text-xs text-white/50 ml-3">
                by APTIM-Solutions
              </span>
            </div>
          </div>
          <form method="post" action="/survey/start">
            <button
              type="submit"
              className="text-sm font-semibold text-navy bg-white hover:bg-white/90 px-4 py-2 rounded-lg transition-colors"
            >
              Start the survey
            </button>
          </form>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-32 pb-24 px-6 bg-navy relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#001a4a]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-8">
            <Clock className="w-3.5 h-3.5" />
            ~25 minutes &middot; anonymous &middot; free basic report
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            How future-ready is
            <br />
            <span className="text-accent-brand">your organisation?</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Benchmark your organisation
            against peers across six dimensions of future-readiness and receive your personalised
            score instantly.
          </p>
          <form method="post" action="/survey/start">
            <button
              type="submit"
              className="inline-flex items-center gap-2 bg-white text-navy font-bold text-base px-8 py-4 rounded-xl hover:bg-white/90 transition-colors shadow-lg shadow-black/20"
            >
              Start the survey
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        </div>
      </section>

      {/* ─── What you'll get ─── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              What you'll get
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Complete the survey and
              receive actionable insights about your organisation's future-readiness.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {VALUE_CARDS.map((card) => (
              <div key={card.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-navy-faint flex items-center justify-center mx-auto mb-5">
                  <card.icon className="w-7 h-7 text-navy" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-2">{card.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── What we measure ─── */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              Six dimensions of future-readiness
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Grounded in the Rohrbeck
              framework for corporate foresight maturity, the survey measures your organisation
              across six research-validated dimensions.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CONSTRUCTS.map((c) => (
              <div
                key={c.label}
                className="bg-white rounded-xl p-5 border border-border-light text-center hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-navy-faint flex items-center justify-center mx-auto mb-3">
                  <c.icon className="w-5 h-5 text-navy" />
                </div>
                <div className="text-sm font-semibold text-navy">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">How it works</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {STEPS.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-12 h-12 rounded-full bg-navy text-white text-xl font-extrabold flex items-center justify-center mx-auto mb-5">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-navy mb-2">{step.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Credibility strip ─── */}
      <section className="py-14 px-6 border-y border-border-light bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-text-muted mb-4">
            Developed by
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-6">
            {/* Placeholder logo — replace with real asset */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className="text-lg font-bold text-navy">APTIM-Solutions</span>
            </div>
          </div>
          <p className="text-sm text-text-secondary max-w-xl mx-auto">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Built on the Rohrbeck
            framework for corporate foresight maturity — peer-reviewed and validated across
            multiple sectors.
          </p>
        </div>
      </section>

      {/* ─── Archetypes preview ─── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              Which archetype is your organisation?
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Your responses are scored
              across multiple dimensions to place your organisation in one of four archetypes.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "Vigilant",
                color: "bg-emerald-500",
                textColor: "text-emerald-700",
                bgColor: "bg-emerald-50",
                description:
                  "Lorem ipsum dolor sit amet. Strong foresight and the ability to act on it.",
              },
              {
                name: "Vulnerable",
                color: "bg-amber-500",
                textColor: "text-amber-700",
                bgColor: "bg-amber-50",
                description:
                  "Lorem ipsum dolor sit amet. Moderate capabilities with room for improvement.",
              },
              {
                name: "Neurotic",
                color: "bg-orange-500",
                textColor: "text-orange-700",
                bgColor: "bg-orange-50",
                description:
                  "Lorem ipsum dolor sit amet. Sees the future clearly but struggles to act on it.",
              },
              {
                name: "In Danger",
                color: "bg-red-500",
                textColor: "text-red-700",
                bgColor: "bg-red-50",
                description:
                  "Lorem ipsum dolor sit amet. Significant gaps in foresight and adaptability.",
              },
            ].map((archetype) => (
              <div
                key={archetype.name}
                className={`${archetype.bgColor} rounded-xl p-6 border border-border-light`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-3 h-3 rounded-full ${archetype.color}`} />
                  <h3 className={`text-lg font-bold ${archetype.textColor}`}>{archetype.name}</h3>
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {archetype.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 px-6 bg-navy">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-extrabold text-white text-center mb-12">
            Frequently asked questions
          </h2>
          <div>
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-navy mb-4">
            Ready to benchmark your organisation?
          </h2>
          <p className="text-text-secondary mb-8 max-w-xl mx-auto">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Takes approximately 25 minutes.
            Your results are available immediately.
          </p>
          <form method="post" action="/survey/start">
            <button
              type="submit"
              className="inline-flex items-center gap-2 bg-navy text-white font-bold text-base px-8 py-4 rounded-xl hover:bg-navy-light transition-colors shadow-lg shadow-navy/20"
            >
              Start the survey
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-text-muted">
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> ~25 minutes
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="w-4 h-4" /> Anonymous
            </span>
            <span className="flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> Free basic report
            </span>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-8 px-6 border-t border-border-light bg-surface">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-text-muted">
            &copy; {new Date().getFullYear()} APTIM-Solutions. All rights reserved.
          </div>
          <div className="flex items-center gap-6 text-sm text-text-muted">
            <a href="/privacy" className="hover:text-navy transition-colors">
              Privacy notice
            </a>
            <a href="/terms" className="hover:text-navy transition-colors">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
